/* eslint-disable no-unused-vars */

import { useEffect, useMemo, useState, useRef } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { store } from 'redux/store';

import PageContainer from '@components/page-container';
import PageHeader from '@components/page-header';
import NewTabs from '@components/new-tabs';
import MoreInformation from '@components/more-information';
import StructureSite from './structure-site';
import Icon from '@components/icon-component';
import LoadingRound from '@components/loading-round/loading-round';
import StructureCard from '@components/structure-listing-inner/structure-card';
import WorkOrder from '../../site-details/tasks/work-order';
import StructureBookmark from '@components/structure-listing/structure-bookmark';
import EquipmentListing from '@components/equipment-listing';
import BookmarkErrorScreen from '@components/bookmark-error-screen';

import { getAppDispatch } from '@utilities/dispatch.util';
import { backHistoryActions } from '@redux/slices/back-history';
import { filterActions } from '@redux/slices/filters';
import { initWebWorker } from 'webworker';

import useWindowDimensions from '@hooks/useWindowDimensions';

import style from './style.module.scss';
import './structure-site.scss';
import DeleteModal from '@components/modal/variants/delete-modal';
import { useToastContext } from '@context/toaster.context';
import BookmarksService from '@services/bookmarks.service';
import { bookmarksActions } from '@redux/slices/bookmarks';

const StructureSiteDetails = () => {
  const [searchParams] = useSearchParams();
  const dispatch = getAppDispatch();
  const { width } = useWindowDimensions();
  const navigate = useNavigate();
  const { ToasterUtil } = useToastContext();

  const swipeAbleMenuRef = useRef(null);
  const mainDivRef = useRef(null);
  const siteCardRef = useRef(null);
  const assetDetailPageHeaderRef = useRef(null);

  const [tab, setTab] = useState('0');
  const [open, setOpen] = useState(false);
  const [moreInfo, setMoreInfo] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [allTask, setAllTask] = useState(false);
  const [checks, setChecks] = useState(false);
  const [inspections, setInspections] = useState(false);
  const [workOrder, setWorkOrder] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const [structureDetails, setStructureDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [workRequest, setWorkRequest] = useState(false);
  const [deleteBookmark, setDeleteBookmark] = useState(false);
  const [newOrEditAssets, setNewOrEditAssets] = useState(false);

  const { sitesMap = {}, sites: cacheSites = [] } = useSelector((state) => state.cache);
  const structureDetailsInfo = useSelector((state) => state?.backHistory?.structureDetails);
  const { tasksFilterCount = 0, equipmentFilterCount = 0 } = useSelector((state) => state.filters);
  const userId = useSelector((state) => state.auth.userId) || '';
  const [isFilterActive, setFilterActive] = useState(width < 768 ? false : true);
  const [isFilterActiveForEquipments, setFilterActiveForEquipments] = useState(
    width < 768 ? false : true
  );
  const { tasksList: reduxTasksList = [], selectTask } = useSelector((state) => state.tasks);
  const { inspectionsList } = useSelector((state) => state.inspection);
  const { checksList } = useSelector((state) => state.checks);
  const { utFormDownloadingLoading } = useSelector((state) => state.utFormData) || {};

  const { id } = useParams();
  const { pathname, search } = useLocation();
  const bookmarkName = searchParams.get('bookmarkName');
  const bookmarkTab = searchParams.get('bookmarkTab');
  const bookmarkId = searchParams.get('bookmarkId');
  const isBookmarkAtAGlance = searchParams.get('isBookmarkAtAGlance') || false;

  const currentOpenTaskTab = searchParams.get('currentOpenTaskTab') || '';

  useEffect(() => {
    if (currentOpenTaskTab) {
      if (currentOpenTaskTab === 'allTask') setAllTask(true);
      else if (currentOpenTaskTab === 'workOrder') setWorkOrder(true);
      else if (currentOpenTaskTab === 'inspections') setInspections(true);
      else if (currentOpenTaskTab === 'checks') setChecks(true);
      else if (currentOpenTaskTab === 'newOrEditAssets') setNewOrEditAssets(true);
    }
  }, [currentOpenTaskTab]);

  useEffect(() => {
    if (!_.isEmpty(structureDetailsInfo)) {
      structureDetailsInfo?.structureDetailsTab &&
        setTab(structureDetailsInfo?.structureDetailsTab || '0');
      structureDetailsInfo?.workOrder && setWorkOrder(structureDetailsInfo?.workOrder);
      structureDetailsInfo?.newOrEditAssets &&
        setNewOrEditAssets(structureDetailsInfo?.newOrEditAssets);
    }
  }, [structureDetailsInfo]);

  useEffect(() => {
    if (utFormDownloadingLoading) {
      return;
    }

    initWebWorker({
      args: {
        store: { ...store.getState() },
        structureId: id,
        userId,
        getWorkRequestData: true
      },
      type: 'get-single-structure-details'
    })
      .then((data) => {
        setStructureDetails(data?.result);
        setIsLoading(false);
        setTab('0');
      })
      .catch((error) => {
        setIsLoading(false);
        setTab('0');
      });
  }, [id, syncComplete, reduxTasksList, inspectionsList, checksList, utFormDownloadingLoading]);

  const {
    name,
    type,
    tasks,
    allDoneTasks,
    isOverDue,
    allWorkOrders,
    allDoneWorkOrders,
    isWorkOrderOverDue,
    status,
    equipments,
    allChecks,
    allDoneChecks,
    structuresMap,
    equipmentsMap,
    dataPoints,
    siteId,
    dataPointsMap,
    MoreInfoDataPointsMap,
    equipmentAllFieldData,
    sitesLoading,
    link,
    isCheckOverDue,
    newWorkRequestList,
    allOnDemandRecordCount,
    allEditEquipmentAssets
  } = useMemo(() => {
    return structureDetails || {};
  }, [structureDetails]);

  useEffect(() => {
    if (syncComplete) setSyncComplete(false);
  }, [syncComplete]);

  const itemsMap = useMemo(() => {
    if (!isLoading)
      return [
        ...(tasks?.length > 0 || allEditEquipmentAssets?.length > 0
          ? [
              {
                label: `Tasks (${tasks?.length + allEditEquipmentAssets?.length})`,
                keyName: 'tasks'
              }
            ]
          : []),

        ...(equipments?.length > 0
          ? [
              {
                label: `Equipment (${equipments?.length})`,
                keyName: 'equipment'
              }
            ]
          : [])
      ];
    else return [];
  }, [isLoading, structureDetails, tasks, equipments]);

  const handleInfoClick = () => {
    const infoTabIndex = items?.findIndex((x) => x?.label === 'Info');
    setTab(infoTabIndex);
  };

  const handleBookmarkClick = () => {
    setOpenModal(true);
  };

  const StructureComponent = ({
    moreInfoTabActive,
    showCard = false,
    className = '',
    isInfo = false
  }) =>
    showCard ? (
      <div className={`marginClass ${className}`} ref={siteCardRef}>
        <StructureCard
          structureDetails
          chips={false}
          title={type}
          subtitle={name}
          hideLocation={true}
          dataPoints={dataPoints || []}
          allTasksForSite={tasks}
          allDoneTasks={allDoneTasks}
          isOverDue={isOverDue}
          status={status}
          sites={{
            ...cacheSites[sitesMap[siteId]],
            link: `/site-details/${siteId}`
          }}
          open={open}
          setOpen={setOpen}
          moreInfo={moreInfo}
          equipments={equipments}
          link={''}
          allOnDemandRecordCount={allOnDemandRecordCount + (allEditEquipmentAssets?.length || 0)}
          onInfoCLick={handleInfoClick}
          onBookmarkCLick={handleBookmarkClick}
          infoTabActive={moreInfoTabActive}
          toDesktopLink={link && width >= 768 ? link : undefined}
          otherTabsView={
            allTask || checks || workOrder || workRequest || inspections || newOrEditAssets
          }
          className={`${isInfo && style.borderBottomNone}`}
        />
      </div>
    ) : (
      <></>
    );

  const items = useMemo(() => {
    return !isLoading
      ? [
          ...(tasks?.length > 0 || allOnDemandRecordCount > 0 || allEditEquipmentAssets?.length
            ? [
                {
                  label: `Tasks + records  (${
                    tasks?.length + allOnDemandRecordCount + allEditEquipmentAssets?.length
                  })`,
                  keyName: 'tasks',
                  children: (
                    <StructureSite
                      setAllTask={setAllTask}
                      setWorkOrder={setWorkOrder}
                      allWorkOrderForStructure={allWorkOrders}
                      allDoneWorkOrder={allDoneWorkOrders}
                      tasks={tasks}
                      allDoneTasks={allDoneTasks}
                      isOverDue={isOverDue}
                      isWorkOrderOverDue={isWorkOrderOverDue}
                      allDoneChecks={allDoneChecks}
                      allChecks={allChecks}
                      isCheckOverDue={isCheckOverDue}
                      setChecks={setChecks}
                      newWorkRequestList={newWorkRequestList}
                      setWorkRequest={setWorkRequest}
                      allOnDemandRecordCount={
                        allOnDemandRecordCount + allEditEquipmentAssets?.length
                      }
                      newWorkOrderList={structureDetails?.newWorkOrderList || []}
                      newChecksList={structureDetails?.newChecksList || []}
                      setInspections={setInspections}
                      allDoneInspection={structureDetails?.allDoneInspections || []}
                      allInspectionForSite={structureDetails?.allInspections || []}
                      isInspectionOverDue={structureDetails?.isInspectionOverDue || []}
                      allEditEquipmentAssets={allEditEquipmentAssets}
                      setNewOrEditAssets={setNewOrEditAssets}
                      card={() => (
                        <StructureComponent
                          moreInfoTabActive={false}
                          showCard={width <= 768 ? true : false}
                          className={`mb16 ${style.pt0}`}
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(equipments?.length > 0
            ? [
                {
                  label: `Equipment (${equipments.length})`,
                  keyName: 'equipment',
                  children: (
                    <EquipmentListing
                      assetDetailsOfEquipmentLists={equipmentAllFieldData}
                      isFilterActiveHeader={isFilterActiveForEquipments}
                      setFilterActiveHeader={setFilterActiveForEquipments}
                      from={pathname}
                      structureId={id}
                      detailPage
                      detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'equipment'}
                      noDistanceTab
                      card={() => (
                        <StructureComponent
                          moreInfoTabActive={false}
                          className={` ${style.pt0}`}
                          showCard={
                            !(
                              tasks?.length > 0 ||
                              allOnDemandRecordCount > 0 ||
                              allEditEquipmentAssets?.length
                            ) && width <= 768
                              ? true
                              : false
                          }
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(MoreInfoDataPointsMap
            ? [
                {
                  label: `Info`,
                  keyName: 'info',
                  children: (
                    <div className={style.infoTab} id="structure-info-details">
                      <StructureComponent
                        moreInfoTabActive={true}
                        showCard={width <= 768 ? true : false}
                        className={`${style.pt0}`}
                        isInfo
                      />

                      <div className={style.addMarginBottom}>
                        <MoreInformation
                          info={Object?.keys(MoreInfoDataPointsMap)?.map((a) => {
                            return {
                              title: MoreInfoDataPointsMap[a]?.value || null,
                              heading: MoreInfoDataPointsMap[a]?.title || null
                            };
                          })}
                          hideEditBtn
                        />
                      </div>
                    </div>
                  )
                }
              ]
            : [])
        ]
      : [];
  }, [
    isLoading,
    itemsMap,
    isFilterActive,
    isFilterActiveForEquipments,
    allDoneTasks,
    tasks,
    newWorkRequestList,
    allOnDemandRecordCount,
    structureDetails
  ]);

  const tabIsActive = useMemo(() => {
    return isFilterActiveForEquipments || false;
  }, [isFilterActiveForEquipments]);

  useEffect(() => {
    if (width <= 768) {
      const elements = document.getElementsByClassName('react-swipeable-view-container');
      for (let i = 0; i < elements.length; i++) {
        let element = elements[i];
        if (tasks?.length > 0) {
          if (tab === 1) element.style.transform = tabIsActive ? 'none' : 'translate(-100%, 0px)';
        } else {
          if (parseInt(tab) === 0)
            element.style.transform = tabIsActive ? 'none' : 'translate(0px, 0px)';
        }
      }
    }
  }, [tabIsActive, width]);

  useMemo(() => {
    if (!isLoading) {
      if (!sitesLoading && !id) return <Navigate to={'/structures'} />;
    }
  }, [structureDetails, isLoading]);

  const onParentClickEvent = ({ workOrderTab, workOrderSortTab }) => {
    dispatch?.(
      backHistoryActions.setStructureDetailsPageHistory({
        workOrder,
        structureDetailsTab: tab,
        workOrderTab,
        workOrderSortTab,
        newOrEditAssets
      })
    );
  };

  useEffect(() => {
    if (items?.length > 0) {
      const params = new URLSearchParams(search);
      const currentTab = parseInt(tab);
      params.set('tab', currentTab);

      if (items[currentTab]?.keyName === 'tasks') {
        if (allTask) params.set('currentOpenTaskTab', 'allTask');
        else if (workOrder) params.set('currentOpenTaskTab', 'workOrder');
        else if (inspections) params.set('currentOpenTaskTab', 'inspections');
        else if (checks) params.set('currentOpenTaskTab', 'checks');
        else if (workRequest) params.set('currentOpenTaskTab', 'workRequest');
        else if (newOrEditAssets) params.set('currentOpenTaskTab', 'newOrEditAssets');
        else if (!workOrder && !allTask && !inspections && !checks && !newOrEditAssets)
          params.delete('currentOpenTaskTab');
      }

      params.set(
        'virtualScrollTab',
        currentTab === 0 && items[currentTab]?.keyName === 'tasks'
          ? -1
          : tasks?.length > 0
          ? currentTab - 1
          : currentTab
      );
      params.set('keyName', items[tab]?.keyName);
      const tabParams = '?' + params.toString();
      const prevRoute = `${pathname}${tabParams}`;
      navigate(prevRoute, { replace: true });
    }
  }, [tab, items, items, workOrder, allTask, inspections, checks, workRequest, newOrEditAssets]);

  useEffect(() => {
    if (!isLoading && bookmarkTab) setTab(bookmarkTab);
  }, [isLoading, bookmarkTab]);

  const pageHeaderClickEvent = () => {
    workOrder && dispatch?.(filterActions.setTaskFilters(null));
    if (moreInfo) setMoreInfo(false);
    else if (allTask) setAllTask(false);
    else if (checks) setChecks(false);
    else if (workOrder) setWorkOrder(false);
    else if (workRequest) setWorkRequest(false);
    else if (inspections) setInspections(false);
    else if (newOrEditAssets) setNewOrEditAssets(false);

    dispatch?.(backHistoryActions.setStructureDetailsPageHistory(null));
  };

  const pageHeaderTitle = useMemo(() => {
    return `${
      bookmarkName
        ? bookmarkName
        : moreInfo
        ? 'Structure Information'
        : allTask
        ? `Tasks, records (${
            tasks?.length > 0 || allOnDemandRecordCount || allEditEquipmentAssets?.length > 0
              ? tasks.length + allOnDemandRecordCount + allEditEquipmentAssets?.length
              : ''
          })`
        : checks
        ? `Checks (${allChecks?.length > 0 ? allChecks?.length : ''})`
        : inspections
        ? `Inspections (${
            structureDetails?.allInspections?.length > 0
              ? structureDetails?.allInspections?.length
              : ''
          })`
        : workOrder
        ? `Work orders (${
            allWorkOrders?.length > 0 || structureDetails?.newWorkOrderList?.length > 0
              ? allWorkOrders.length + structureDetails?.newWorkOrderList?.length
              : ''
          })`
        : workRequest
        ? `Work requests (${newWorkRequestList?.length > 0 ? newWorkRequestList?.length : ''})`
        : newOrEditAssets
        ? `New or edited assets (${
            allEditEquipmentAssets?.length > 0 ? allEditEquipmentAssets?.length : ''
          })`
        : 'Structure'
    }`;
  }, [
    bookmarkName,
    moreInfo,
    allTask,
    checks,
    workOrder,
    tasks,
    workRequest,
    newWorkRequestList,
    allOnDemandRecordCount,
    structureDetails,
    newOrEditAssets
  ]);

  const filtersCount = useMemo(() => {
    return items[tab]?.keyName === 'equipment' ? equipmentFilterCount : tasksFilterCount;
  }, [tab, items, equipmentFilterCount, tasksFilterCount]);

  const isFilterIconIsRequired = useMemo(() => {
    return (!items[tab]?.keyName?.toLowerCase()?.includes('tasks') &&
      !items[tab]?.keyName?.toLowerCase()?.includes('info')) ||
      allTask ||
      checks ||
      inspections ||
      workOrder ||
      workRequest ||
      newOrEditAssets
      ? items[tab]?.keyName === 'equipment'
        ? setFilterActiveForEquipments
        : setFilterActive
      : false;
  }, [tab, items, allTask, checks, inspections, workOrder, workRequest, newOrEditAssets]);

  const siteDetailsPageClassNames = useMemo(() => {
    return `${moreInfo || selectTask ? style.pageListingsWithMoreInfo : style.pageListings} ${
      style.noScroll
    } ${
      (workOrder || allTask || checks || inspections || workRequest || newOrEditAssets) &&
      style.noScroll
    }`;
  }, [workOrder, allTask, checks, inspections, moreInfo, selectTask, workRequest, newOrEditAssets]);

  const structureNotFoundInBookmark = useMemo(() => {
    if (bookmarkName && !isLoading) {
      if (name) return false;
      else return true;
    }
    return false;
  }, [bookmarkName, isLoading, name]);

  const allTasksList = useMemo(() => {
    return [
      ...(structureDetails?.tasks || []),
      ...(structureDetails?.newWorkOrderList || []),
      ...(structureDetails?.newWorkRequestList || []),
      ...(structureDetails?.allEditEquipmentAssets || [])
    ];
  }, [allTask, structureDetails]);

  const allWorkOrderList = useMemo(() => {
    return [...(allWorkOrders || []), ...(structureDetails?.newWorkOrderList || [])];
  }, [workOrder, structureDetails, allWorkOrders]);

  const allChecksList = useMemo(() => {
    return [...(allChecks || []), ...(structureDetails?.newChecksList || [])];
  }, [checks, structureDetails, allChecks]);

  const allInspectionsList = useMemo(() => {
    return [...(structureDetails?.allInspections || [])];
  }, [inspections, structureDetails]);

  useEffect(() => {
    if (!isLoading && !name && !status) {
      navigate('/error?assetNotFound=true');
    }
  }, [isLoading, name, status]);

  const onDeleteBookmark = async () => {
    try {
      if (!bookmarkId) {
        ToasterUtil.showError('Unable to delete bookmark: Bookmark ID is missing.');
        return;
      }

      await BookmarksService.deleteBookmarks({ bookmarkId: bookmarkId });

      dispatch?.(bookmarksActions.setBookmarkValidity(true));

      ToasterUtil.showSuccess('Bookmark deleted successfully');

      const params = new URLSearchParams(location.search);

      params.set('from', 'site');
      params.delete('bookmarkName');
      params.delete('bookmarkId');

      setDeleteBookmark(false);

      const newUrl = `${location.pathname}?${params.toString()}`;

      // Use navigate with replace
      navigate(newUrl, { replace: true });
    } catch (error) {
      ToasterUtil.showError('Failed to delete bookmark');
    }
  };

  const { moreInfoTabActive, isTasksTabActive } = useMemo(() => {
    const parseTab = parseInt(tab);
    return {
      moreInfoTabActive: items[parseTab]?.keyName === 'info' ? true : false,
      isTasksTabActive: items[parseTab]?.keyName === 'tasks' ? true : false
    };
  }, [items, tab]);

  const swipeable = document.getElementById('react-swipeable-view-container') || null;
  const structureInfoDetails = document.getElementById('structure-info-details') || null;
  const taskInfoTab = document.getElementById('tasks-info-tab') || null;

  useEffect(() => {
    // const currentScrollPosition = mainDivRef?.current?.scrollTop;
    // const siteCardOffsetHeight = siteCardRef?.current?.offsetHeight || 0;

    // if (siteCardRef?.current && mainDivRef?.current) {
    //   const container =
    //     moreInfoTabActive && structureInfoDetails
    //       ? structureInfoDetails
    //       : isTasksTabActive && taskInfoTab
    //       ? taskInfoTab
    //       : null;

    //   if (container && (moreInfoTabActive || isTasksTabActive)) {
    //     const topHeaderHeight = width > 769 ? 94 : 86;
    //     const siteCardAndMarginCalculate = siteCardOffsetHeight || 0;
    //     const navigationTabsHeight = 33;
    //     const bottomFooterHeight = width > 769 ? 0 : 84;

    //     const totalHeight =
    //       topHeaderHeight + siteCardAndMarginCalculate + bottomFooterHeight + navigationTabsHeight;

    //     container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition}px)`;
    //   }

    if (swipeable) {
      if (moreInfoTabActive) {
        swipeable.style.background = '#ffffff';
      } else {
        swipeable.style.background = '';
      }
    }
  }, [
    // mainDivRef,
    // tab,
    moreInfoTabActive,
    swipeable
    // structureInfoDetails,
    // taskInfoTab,
    // isTasksTabActive,
    // siteCardRef
  ]);

  const onScrollParentEvent = (event) => {
    const currentScrollPosition = event.target.scrollTop || 0;
    const siteCardOffsetHeight = siteCardRef?.current?.offsetHeight || 0;

    if (siteCardRef?.current) {
      const topHeaderHeight = width > 769 ? 94 : 86;
      const siteCardAndMarginCalculate = siteCardOffsetHeight || 0;
      const navigationTabsHeight = 33;
      const bottomFooterHeight = width > 769 ? 0 : 84;
      const totalHeight =
        topHeaderHeight + siteCardAndMarginCalculate + bottomFooterHeight + navigationTabsHeight;

      const container =
        moreInfoTabActive && structureInfoDetails
          ? structureInfoDetails
          : isTasksTabActive && taskInfoTab
          ? taskInfoTab
          : null;
      if (container && (moreInfoTabActive || isTasksTabActive)) {
        container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition}px)`;
      }

      const containerIds = [
        'structure-listing-pages-container',
        'site-listing-pages-container',
        'pipeline-listing-pages-container',
        'equipment-listing-pages-container'
      ];

      containerIds.forEach((id) => {
        const container = document.getElementById(id);
        if (container) {
          container.style.height = `calc(100svh - ${totalHeight - currentScrollPosition + 10}px)`;
        }
      });
    }

    const tasksRecordsListsTabs = document?.querySelectorAll('#tasksRecordsListsTabs');
    for (let a = 0; a < tasksRecordsListsTabs.length; a++) {
      const totalHeight =
        tasksRecordsListsTabs[a]?.getAttribute('data-pageHeightOnDetailsPage') || 0;
      tasksRecordsListsTabs[a].style.height = `calc(100svh  - ${
        totalHeight - currentScrollPosition
      }px)`;
    }
  };

  return (
    <>
      {!isLoading && structureNotFoundInBookmark ? (
        <BookmarkErrorScreen />
      ) : (
        <PageContainer moreInfo={moreInfo}>
          <PageHeader
            disableBack={
              allTask || checks || inspections || workOrder || workRequest || newOrEditAssets
            }
            title={pageHeaderTitle}
            {...(isFilterIconIsRequired && {
              setFilterActive: isFilterIconIsRequired
            })}
            onClick={pageHeaderClickEvent}
            count={filtersCount}
            ref={assetDetailPageHeaderRef}
          />

          {isLoading ? (
            <div style={{ backgroundColor: 'transparent', width: '100%' }}>
              <LoadingRound />
            </div>
          ) : (
            <div
              ref={mainDivRef}
              id="assetDetailPage"
              className={siteDetailsPageClassNames}
              style={{ position: 'relative', paddingBottom: '0px', marginTop: '0px' }}
              onScroll={onScrollParentEvent}
            >
              <StructureComponent
                moreInfoTabActive={moreInfoTabActive}
                showCard={width > 768 ? true : false}
              />
              {moreInfo ? (
                <>
                  <MoreInformation
                    info={Object?.keys(MoreInfoDataPointsMap)?.map((a) => {
                      return {
                        title: MoreInfoDataPointsMap[a]?.value || null,
                        heading: MoreInfoDataPointsMap[a]?.title || null
                      };
                    })}
                  />
                </>
              ) : (
                <div className="flex relative">
                  <div
                    style={{
                      marginTop: '0px',
                      overflow: 'hidden',
                      paddingBottom: '0px'
                    }}
                    className={`${style.pageListings} mobileMargin`}
                  >
                    {allTask ||
                    workOrder ||
                    checks ||
                    workRequest ||
                    inspections ||
                    newOrEditAssets ? (
                      <WorkOrder
                        asset="structure"
                        tasks={
                          inspections
                            ? allInspectionsList
                            : allTask
                            ? allTasksList
                            : checks
                            ? allChecksList
                            : workRequest
                            ? newWorkRequestList
                            : workOrder
                            ? allWorkOrderList
                            : newOrEditAssets
                            ? allEditEquipmentAssets
                            : []
                        }
                        isFilterActiveHeader={isFilterActive}
                        setFilterActiveHeader={setFilterActive}
                        setSyncComplete={setSyncComplete}
                        selectTodoOrInProgressStatus={!_.isEmpty(bookmarkName) ? true : false}
                        onParentClickEvent={onParentClickEvent}
                        workOrderSelectedTab={structureDetails?.workOrderTab || null}
                        workOrderSortTab={structureDetails?.workOrderSortTab || null}
                        siteDetails
                        noDistanceTab
                        workRequest={workRequest}
                        newOrEditAssets={newOrEditAssets}
                        filterType={
                          allTask
                            ? 'all'
                            : checks
                            ? 'check'
                            : workRequest
                            ? 'workRequest'
                            : inspections
                            ? 'inspection'
                            : newOrEditAssets
                            ? 'newOrEditAssets'
                            : null
                        }
                      />
                    ) : items?.length > 0 ? (
                      <div
                        className={`assetDetailNavNotFixed`}
                        ref={swipeAbleMenuRef}
                        id="assetDetailsNavigation"
                      >
                        <NewTabs
                          active={tab}
                          tabs={items}
                          setActive={(tab) => setTab(tab)}
                          className="mobileMargin"
                          swipeAbleNavbarWhiteBackground={moreInfoTabActive ? true : false}
                        />
                      </div>
                    ) : (
                      <>
                        <div
                          className="no-result"
                          style={{ flex: 1, justifyContent: 'flex-start', gap: '20px' }}
                        >
                          <div>No results</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {openModal && (
            <StructureBookmark
              open={openModal}
              setOpen={(value) => {
                setOpenModal(value);
              }}
              placeholder="My favourite truck"
              type="Structure Details"
              structureId={id || null}
              editMode={bookmarkId}
              bookmarkPrefillName={bookmarkName}
              isBookmarkAtAGlance={isBookmarkAtAGlance}
              onDelete={() => {
                setDeleteBookmark(true);
                setOpenModal(false);
              }}
            />
          )}
          {deleteBookmark && (
            <DeleteModal
              open={deleteBookmark}
              setOpen={setDeleteBookmark}
              handleDelete={onDeleteBookmark}
              confirmDeletionBookmark
              recordType="Bookmark"
            />
          )}
        </PageContainer>
      )}
    </>
  );
};

export default StructureSiteDetails;
